import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { resolveImage, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Trash2, ImageIcon, Star } from "lucide-react";

export default function AdminMedia() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const { data: media } = useQuery({ queryKey: ["media"], queryFn: async () => (await api.get("/admin/media")).data });
  const { data: settings } = useQuery({ queryKey: ["brand"], queryFn: async () => (await api.get("/settings/public")).data });
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Image uploaded"); qc.invalidateQueries(["media"]); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/media/${id}`); toast.success("Deleted"); qc.invalidateQueries(["media"]); }
    catch (err) {
      const d = err.response?.data?.detail || "";
      if (err.response?.status === 409 && window.confirm(`${d}\n\nForce delete anyway?`)) {
        try { await api.delete(`/admin/media/${id}?force=true`); toast.success("Force-deleted"); qc.invalidateQueries(["media"]); } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
      } else toast.error(formatApiError(d));
    }
  };
  const setBrand = async (field, url) => {
    try { await api.put("/admin/brand", { [field]: url }); toast.success("Brand asset updated"); qc.invalidateQueries(["brand"]); qc.invalidateQueries(["settings"]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!media) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const logo = settings?.logo_url;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Media Library</h1>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-file" />
          <Button className="bg-teal hover:bg-teal-dark text-white gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload image
          </Button>
        </div>
      </div>
      {logo && <div className="mb-4 text-xs text-slate-400 flex items-center gap-2"><Star className="w-3.5 h-3.5 text-amber-400" /> Current site logo set.</div>}
      {media.length === 0 ? (
        <div className="bg-slate-900 border border-white/10 rounded-xl p-10 text-center text-slate-500"><ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" /> No media yet. Upload your first image.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((m) => (
            <div key={m.id} className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden group" data-testid={`media-${m.id}`}>
              <div className="aspect-square bg-slate-800 flex items-center justify-center overflow-hidden">
                <img src={resolveImage(m.url)} alt={m.filename} className="w-full h-full object-contain" />
              </div>
              <div className="p-2">
                <div className="text-[11px] text-slate-400 truncate">{m.filename}</div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => setBrand("logo_url", m.url)} className="flex-1 text-[10px] bg-white/5 hover:bg-white/10 text-slate-300 rounded px-1.5 py-1" data-testid={`media-logo-${m.id}`}>Set logo</button>
                  <button onClick={() => setBrand("invoice_logo_url", m.url)} className="flex-1 text-[10px] bg-white/5 hover:bg-white/10 text-slate-300 rounded px-1.5 py-1">Invoice logo</button>
                  <button onClick={() => del(m.id)} className="text-red-400 hover:text-red-300 px-1.5" data-testid={`media-del-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
